import { FunctionComponent, memo } from "react";
import { IProps } from "./client-list.types";
import { useUser } from "hooks-for/users/use-user";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { axios } from "utilities/axios";
import { Route } from "@edgetier/watchtower-types";
import toast from "react-hot-toast";
import { getServerError } from "@edgetier/utilities";
import useUsersClients from "hooks-for/users/use-users-clients";
import { SpinnerUntil } from "@edgetier/components";
import "./client-list.scss";
import { resetStorage } from "utilities-for/persisted-storage/reset-storage";
import { PageNames } from "types-for/routes";
import { AbsolutePaths } from "constants/routes";

/**
 * A list of clients that the user can choose between.
 */
const ClientList: FunctionComponent<IProps> = () => {
    const { data: clients, isLoading: areClientsLoading } = useUsersClients(
        {},
        { select: (clients) => clients.sort((one, two) => one.client.localeCompare(two.client)) }
    );
    const { data: user, isLoading: isUserLoading } = useUser();

    /**
     * Make a PATCH request to the backend to change the user's client and reload the page.
     * @param clientId The ID of the client that the user is changing to.
     */
    const changeClient = async (clientId: string) => {
        try {
            await axios.patch(Route.UsersMe, { clientId });
            resetStorage();
            window.location.assign(AbsolutePaths[PageNames.AuthenticateRefresh]());
        } catch (error) {
            toast.error(getServerError(error));
        }
    };

    return (
        <SpinnerUntil data={clients} isReady={!areClientsLoading && !isUserLoading}>
            {typeof clients === "undefined" ? (
                <div>Could not load clients</div>
            ) : (
                <div className="client-list">
                    {clients.map((client) => (
                        <div
                            className={classNames("client-list__client", {
                                "client-list__client--active": client.clientId === user?.clientId,
                            })}
                            onClick={() => changeClient(client.clientId)}
                            key={client.clientId}
                        >
                            <div className="client-list__client-name">{client.client}</div>
                            {client.clientId === user?.clientId && <FontAwesomeIcon icon={faCheck} />}
                        </div>
                    ))}
                </div>
            )}
        </SpinnerUntil>
    );
};

export default memo(ClientList);
